import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import { Link, graphql } from "gatsby";
import { Button, Col, Container, Row } from "react-bootstrap";
import { ArrowRight } from "react-bootstrap-icons";
import classNames from "classnames";

import GatsbyImageWithSVGSupport from "./GatsbyImageWithSVGSupport";
import RichTextBlock from "./RichTextBlock";
import BusinessInfoModalForm from "./BusinessInfoModalForm";

import { openModal, selectModalId } from "../redux/slices/businessModalSlice";

import Arrow from "../images/icons/arrow-narrow.svg";

import "../styles/components/IconTiles.scss";

const IconTileRedirect = ({ url, urlText, gatedForm, pdf, modalId }) => {
    const dispatch = useDispatch();
    const redirectProps = {
        variant: "link",
        className: "mt-auto",
    };

    if (gatedForm && pdf) {
        redirectProps["aria-label"] = "Open gated form";
        redirectProps.onClick = () =>
            dispatch(
                openModal({
                    modalId,
                    downloadPdfUrl: pdf.file.url,
                    source: gatedForm.toLowerCase().replace(/\s+/g, "_"),
                }),
            );
    } else if (pdf) {
        redirectProps.as = "a";
        redirectProps["aria-label"] = pdf.fileName;
        redirectProps.href = pdf.file.url;
        redirectProps.target = "_blank";
        redirectProps.rel = "noreferrer";
    } else if (url?.startsWith("/")) {
        redirectProps.as = Link;
        redirectProps.to = url;
    } else if (url) {
        redirectProps.as = "a";
        redirectProps.href = url;
    } else {
        return null;
    }

    if (urlText) {
        return (
            <Button {...redirectProps}>
                {urlText}
                <ArrowRight className="ms-2" size={16} />
            </Button>
        );
    }

    return (
        <Button
            {...redirectProps}
            variant="outline-primary"
            className="url-button"
        >
            <Arrow />
        </Button>
    );
};

IconTileRedirect.propTypes = {
    modalId: PropTypes.string,
    url: PropTypes.string,
    urlText: PropTypes.string,
    gatedForm: PropTypes.string,
    pdf: PropTypes.shape({
        description: PropTypes.string,
        file: PropTypes.shape({
            url: PropTypes.string.isRequired,
            fileName: PropTypes.string,
        }),
    }),
};

const IconTiles = props => {
    const headingRef = useRef(null);
    const cardsRef = useRef(null);
    const [hasAnimatedHeading, setHasAnimatedHeading] = useState(false);
    const [hasAnimatedCards, setHasAnimatedCards] = useState(false);
    const modalId = useSelector(selectModalId);
    const section = props;

    const observeElement = (element, hasAnimated, setHasAnimated) => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !hasAnimated) {
                    setHasAnimated(true);
                    observer.disconnect(); // Stop observing after animation is triggered
                }
            },
            { threshold: 0.1 },
        );

        if (element) {
            observer.observe(element);
        }

        // Cleanup function using the captured local variable
        return () => {
            if (element) {
                observer.unobserve(element);
            }
        };
    };

    useEffect(() => {
        observeElement(
            headingRef.current,
            hasAnimatedHeading,
            setHasAnimatedHeading,
        );
        observeElement(cardsRef.current, hasAnimatedCards, setHasAnimatedCards);
    }, [hasAnimatedCards, hasAnimatedHeading]);

    if (!section) return null;

    const iconTiles = section.iconTiles;

    const classNamesTiles = section.tilesStyle
        ?.map(s => s.toLowerCase().replace(" ", "-"))
        .join(" ");

    const theme = section.theme?.replace(/\s/g, "-").toLowerCase();

    return (
        <div
            id={section.anchorId}
            className={classNames("icon-tiles", theme, {
                "py-5": theme,
            })}
        >
            <Container>
                {(section.hero || section.title || section.leadText) && (
                    <Row
                        ref={headingRef}
                        className={classNames(
                            "full-opacity align-items-center mb-5",
                            {
                                "fade-in-up": hasAnimatedHeading,
                            },
                        )}
                    >
                        {section.hero && (
                            <Col className="mb-3" md={4}>
                                <GatsbyImageWithSVGSupport
                                    image={section.hero}
                                />
                            </Col>
                        )}
                        {(section.tile || section.leadText) && (
                            <Col
                                md={section.hero ? 8 : 12}
                                className={classNames("text-start", {
                                    "text-center": !section.hero,
                                })}
                            >
                                {section.title && <h2>{section.title}</h2>}
                                {section.leadText && (
                                    <RichTextBlock content={section.leadText} />
                                )}
                            </Col>
                        )}
                    </Row>
                )}
                <Row
                    ref={cardsRef}
                    className={classNames(
                        "full-opacity justify-content-evenly",
                        {
                            "fade-in-up": hasAnimatedCards,
                        },
                    )}
                >
                    {iconTiles.map(item => (
                        <Col
                            className="mb-3 mb-md-5"
                            key={item.id}
                            xs={12}
                            md={4}
                        >
                            <div
                                className={classNames(
                                    "icon-tile p-3",
                                    classNamesTiles,
                                )}
                            >
                                <GatsbyImageWithSVGSupport
                                    image={item.image}
                                    className="image"
                                />
                                {item.title && (
                                    <h4 className="my-3">{item.title}</h4>
                                )}
                                {item.copy && (
                                    <RichTextBlock content={item.copy} />
                                )}
                                <IconTileRedirect
                                    modalId={item.id}
                                    url={item.url}
                                    urlText={item.urlText}
                                    gatedForm={item.gatedForm}
                                    pdf={item.pdf}
                                />
                                {modalId === item.id && (
                                    <BusinessInfoModalForm />
                                )}
                            </div>
                        </Col>
                    ))}
                    {section.button && (
                        <Col xs={12} className="text-center mt-3">
                            <Button
                                to={section.button.url}
                                as={Link}
                                variant="primary"
                                type="submit"
                                size="lg"
                            >
                                {section.button.label}
                            </Button>
                        </Col>
                    )}
                </Row>
            </Container>
        </div>
    );
};

export const query = graphql`
    fragment DynamicIconTiles on ContentfulIconTilesSection {
        sys {
            contentType {
                sys {
                    id
                }
            }
        }
        title
        anchorId
        leadText {
            raw
        }
        hero {
            gatsbyImageData(layout: CONSTRAINED)
            title
            description
            file {
                contentType
                url
            }
        }
        takesFullPageWidth
        theme
        iconTiles {
            id
            title
            copy {
                raw
            }
            image {
                gatsbyImageData(layout: CONSTRAINED)
                title
                description
                file {
                    contentType
                    url
                }
            }
            url
            urlText
            gatedForm
            pdf {
                description
                file {
                    url
                    fileName
                }
            }
        }
        tilesStyle
        button {
            label
            url
        }
    }
`;

IconTiles.propTypes = {
    title: PropTypes.string,
    theme: PropTypes.string,
    leadText: PropTypes.shape({ raw: PropTypes.string.isRequired }),
    hero: PropTypes.shape({
        title: PropTypes.string,
        description: PropTypes.string,
        gatsbyImageData: PropTypes.object,
        file: PropTypes.shape({
            contentType: PropTypes.string.isRequired,
            url: PropTypes.string.isRequired,
        }),
    }),
    iconTiles: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            title: PropTypes.string.isRequired,
            copy: PropTypes.shape({ raw: PropTypes.string.isRequired }),
            image: PropTypes.shape({
                title: PropTypes.string,
                description: PropTypes.string,
                gatsbyImageData: PropTypes.object,
                file: PropTypes.shape({
                    contentType: PropTypes.string.isRequired,
                    url: PropTypes.string.isRequired,
                }),
            }),
            url: PropTypes.string,
            urlText: PropTypes.string,
            gatedForm: PropTypes.string,
            pdf: PropTypes.shape({
                description: PropTypes.string,
                file: PropTypes.shape({
                    url: PropTypes.string.isRequired,
                    fileName: PropTypes.string,
                }),
            }),
        }),
    ),
    button: PropTypes.shape({
        label: PropTypes.string.isRequired,
        url: PropTypes.string.isRequired,
    }),
};

export default IconTiles;
